'use client';

import { useOnClickOutside } from 'usehooks-ts';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  useRef,
  useState,
  type JSX,
  type RefObject,
  type ChangeEventHandler,
  type KeyboardEventHandler,
} from 'react';

import { Show } from '~/components/show';
import { Text } from '~/components/text';
import { Stack } from '~/components/stack';
import { maximumNumber } from '~/constants';
import { Textarea } from '~/components/textarea';
import { useEditable } from '~/hooks/useEditable';
import { cn, combine, formatMoney } from '~/utils';
import { platformSettingsOptions } from '~/features/settings/querties';
import {
  Select,
  SelectItem,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
} from '~/components/select';

interface IEditableProps {
  value?: string;
  className?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

export function EditableInput({
  value,
  onChange,
  className,
  placeholder = 'Double click to edit',
}: IEditableProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);

  const { edit, isEditing, onKeyDown } = useEditable({
    ref: inputRef,
    onUpdate: () => {
      if (!inputRef.current) {
        return;
      }

      onChange?.(inputRef.current.value);
    },
  });

  return (
    <Stack onDoubleClick={edit}>
      <Show when={!isEditing}>
        <Text
          size='sm'
          className={cn('whitespace-pre', !value && 'text-[#bfbfbf]')}
        >
          {value || placeholder}
        </Text>
      </Show>
      <Show when={isEditing}>
        <input
          autoFocus
          ref={inputRef}
          defaultValue={value}
          onKeyDown={onKeyDown}
          className={cn(
            'p-0 min-h-0 h-max border-0 rounded-none text-sm font-normal leading-[130%]',
            'focus-visible:outline-none',
            className,
          )}
        />
      </Show>
    </Stack>
  );
}

export function EditableTextarea({
  value,
  onChange,
  className,
  placeholder = 'Double click to edit',
}: IEditableProps): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { edit, isEditing, onKeyDown } = useEditable({
    ref: textareaRef,
    onUpdate: () => {
      if (!textareaRef.current) {
        return;
      }

      onChange?.(textareaRef.current.value);
    },
  });

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (event) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.altKey)) {
      event.preventDefault();
      const textarea = event.currentTarget;
      const { value } = textarea;
      const nextCursor = textarea.selectionStart + 1;
      const headText = value.slice(0, textarea.selectionStart);
      const tailText = value.slice(textarea.selectionEnd);

      event.currentTarget.value = tailText
        ? combine(
            {
              joinWith: '\n',
            },
            headText,
            tailText,
          )
        : `${headText}\n`;

      textarea.selectionEnd = nextCursor;
      textarea.selectionStart = nextCursor;
    } else {
      onKeyDown(event);
    }
  };

  return (
    <Stack onDoubleClick={edit}>
      <Show when={!isEditing}>
        <Text
          size='sm'
          className={cn('whitespace-pre-line', !value && 'text-[#bfbfbf]')}
        >
          {value || placeholder}
        </Text>
      </Show>
      <Show when={isEditing}>
        <Textarea
          autoFocus
          ref={textareaRef}
          defaultValue={value}
          onKeyDown={handleKeyDown}
          className={cn(
            'p-0 min-h-0 h-max border-0 rounded-none text-sm font-normal leading-[130%]',
            className,
          )}
        />
      </Show>
    </Stack>
  );
}

export function EditablePricing({
  value,
  onChange,
  className,
  placeholder = 'Double click to edit',
  disabled = false,
}: IEditableProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [price, setPrice] = useState<string>(value || '');
  const {
    data: { currency },
  } = useSuspenseQuery(platformSettingsOptions());

  const { edit, isEditing, onKeyDown } = useEditable({
    ref: inputRef,
    onUpdate: () => {
      if (!inputRef.current) {
        return;
      }

      onChange?.(price);
    },
  });

  const handleOnChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    let value = event.target.value
      .replace(/[^\.\d]/g, '')
      .replace(/^(\d+\.\d{2}).*$/, '$1');

    if (Number(value) > maximumNumber) {
      value = maximumNumber.toString();
    }

    setPrice(value);
  };

  return (
    <Stack onDoubleClick={edit} className={cn(disabled && 'opacity-50')}>
      <Show when={!isEditing}>
        <Text
          size='sm'
          className={cn('whitespace-pre', !value && 'text-[#bfbfbf]')}
        >
          {value ? formatMoney({ value: price, currency }) : placeholder}
        </Text>
      </Show>
      <Show when={isEditing}>
        <input
          autoFocus
          ref={inputRef}
          defaultValue={value}
          disabled={disabled}
          onKeyDown={onKeyDown}
          onChange={handleOnChange}
          className={cn(
            'p-0 min-h-0 h-max border-0 rounded-none text-sm font-normal leading-[130%]',
            'focus-visible:outline-none',
            className,
          )}
        />
      </Show>
    </Stack>
  );
}

interface IEditableDropdownProps extends IEditableProps {
  options?: Array<{
    label: string;
    value: string;
  }>;
}

export function EditableDropdown({
  value,
  onChange,
  options = [],
  placeholder = 'Double click to edit',
}: IEditableDropdownProps): JSX.Element {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selected, setSelected] = useState(value || '');

  const groupedOptionsRef = useRef(
    options.reduce<Record<string, string>>((result, option) => {
      result[option.value] = option.label;

      return result;
    }, {}),
  );

  const edit = () => {
    setIsEditing(true);
  };

  useOnClickOutside(buttonRef as RefObject<HTMLButtonElement>, (event) => {
    const target = event.target as Element | null;

    if (!target?.closest?.('[role="listbox"]')) {
      setIsEditing(false);
      onChange?.(selected);
    }
  });

  return (
    <Stack onDoubleClick={edit}>
      <Show when={!isEditing}>
        <Text
          size='sm'
          className={cn('whitespace-pre', !selected && 'text-[#bfbfbf]')}
        >
          {selected ? groupedOptionsRef.current[selected] : placeholder}
        </Text>
      </Show>
      <Show when={isEditing}>
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger ref={buttonRef} className='w-full'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {options.map(({ label, value }) => {
                return (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                );
              })}
            </SelectGroup>
          </SelectContent>
        </Select>
      </Show>
    </Stack>
  );
}
